/**
 * Shared store for the PWA `beforeinstallprompt` event.
 *
 * Browsers fire `beforeinstallprompt` exactly ONCE per page load. To both
 * (a) defer it for our own UI AND (b) not pollute the console on pages that
 * don't have a custom Install button, we use OPT-IN capture:
 *
 *   - A component that owns an Install button calls `enableCapture()`
 *     (idempotent). Until at least one consumer has opted in, we do NOT
 *     intercept the event — Chrome shows its native install banner.
 *   - Once a consumer opts in, future `beforeinstallprompt` events are
 *     intercepted (`preventDefault`) and cached.
 *   - If the event has already fired before opt-in, we still cache it via
 *     a one-shot pre-listener so late mounts don't miss it.
 *
 * `prompt()` is only ever called from a user-gesture handler.
 */

let savedEvent = null;
const listeners = new Set();
let captureEnabled = false;
let preCaptureEvent = null;

const notify = () => {
  for (const cb of listeners) {
    try { cb(savedEvent); } catch (_) { /* swallow */ }
  }
};

if (typeof window !== 'undefined') {
  // Pre-capture: only RECORD the event (no preventDefault). If a consumer
  // later opts in, we promote the recorded event into our cache.
  window.addEventListener('beforeinstallprompt', (e) => {
    if (captureEnabled) {
      e.preventDefault();
      savedEvent = e;
      notify();
    } else {
      preCaptureEvent = e;
    }
  });

  window.addEventListener('appinstalled', () => {
    savedEvent = null;
    preCaptureEvent = null;
    notify();
  });
}

/**
 * Call from any component that owns a custom Install button.
 * Idempotent. Enables interception of future `beforeinstallprompt` events.
 * If we already saw one before opt-in, do NOT promote it: Chrome's native
 * banner may already have been shown and the cached event would be stale.
 */
export const enableInstallCapture = () => {
  if (captureEnabled) return;
  captureEnabled = true;
  // We intentionally do NOT promote preCaptureEvent here — Chrome only
  // allows prompt() to be called once per event, and a pre-capture event
  // that wasn't preventDefault'd is unreliable.
  preCaptureEvent = null;
};

export const subscribeInstallPrompt = (cb) => {
  listeners.add(cb);
  // Fire immediately so late subscribers receive any cached event.
  try { cb(savedEvent); } catch (_) { /* swallow */ }
  return () => { listeners.delete(cb); };
};

export const getInstallPrompt = () => savedEvent;

export const triggerInstallPrompt = async () => {
  if (!savedEvent) return { ok: false, reason: 'no-saved-event' };
  try {
    savedEvent.prompt();
    const choice = await savedEvent.userChoice;
    savedEvent = null;
    notify();
    return { ok: true, outcome: choice.outcome };
  } catch (e) {
    savedEvent = null;
    notify();
    return { ok: false, reason: 'prompt-failed', error: e };
  }
};

export const isStandalone = () =>
  (typeof window !== 'undefined') && (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );

export const isIOS = () =>
  (typeof window !== 'undefined') &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;
